import Component from './Component';

// A parallax effect that could be applied to some images
// Used by the ImageAndText component if parallax toggled 'on' by content author.

export default class ImageParallax extends Component {
  load() {
    this.parallaxItems();
  }

  parallaxItems() {

    const parallaxImages = document.querySelectorAll('.imageandtext__img--parallax');
    if (parallaxImages.length == 0) {
      return;
    }

    const moves = [];

    for (let i = 0; i < parallaxImages.length; i++) {
      const elImage = parallaxImages[i];
      const elFigWindow = elImage.closest('.imageandtext__figure');

      const move = this.imageParallax(elImage, elFigWindow);
      move(); // initial position
      moves.push(move);

      this.guardImageHeight(elImage, elFigWindow);
    }

    // A single scroll listener and a single rAF-pending guard drive all
    // parallax images, no matter how many are in view at once.
    let rafPending = false;
    window.addEventListener('scroll', () => {
      if (!rafPending) {
        rafPending = true;
        window.requestAnimationFrame(() => {
          for (const move of moves) move();
          rafPending = false;
        });
      }
    }, { passive: true });

  }


  // Warns if the parallax image is not taller than the coverage threshold (or only
  // marginally so). Full coverage is possible only when the image is at least as tall
  // as the SMALLER of the frame and the viewport (min(frame, viewport)) - the largest
  // band of the frame that can be visible at once. Comparing to the frame alone is
  // wrong when the frame is taller than the viewport.
  guardImageHeight(elImage, elFigWindow, minMarginPercent = 5) {

    const check = () => {
      const imgHt = elImage.getBoundingClientRect().height;
      const figHt = elFigWindow.getBoundingClientRect().height;
      const winHt = window.innerHeight;
      const required = Math.min(figHt, winHt); // largest visible band of the frame

      if (required === 0) {
        return;
      }

      const marginPercent = ((imgHt - required) / required) * 100;
      const rounded = Math.round(marginPercent * 10) / 10;
      const ref = `required min ${Math.round(required)}px = min(frame ${Math.round(figHt)}px, viewport ${Math.round(winHt)}px)`;

      if (imgHt <= required) {
        console.warn(
          `[ImageParallax] Image (${Math.round(imgHt)}px) is NOT taller than the ${ref}. ` +
          `Margin: ${rounded}%. Parallax will expose gaps.`,
          elImage
        );
      } else if (marginPercent < minMarginPercent) {
        console.warn(
          `[ImageParallax] Image (${Math.round(imgHt)}px) is only ${rounded}% taller than the ${ref}, ` +
          `below the ${minMarginPercent}% margin. Parallax range is very limited.`,
          elImage
        );
      }
    };

    // Check now (in case the image is already loaded) and once it finishes loading.
    if (elImage.complete) {
      check();
    } else {
      elImage.addEventListener('load', check, { once: true });
    }

    // Re-check on resize, since both image and frame heights are viewport-relative.
    window.addEventListener('resize', check);
  }

  imageParallax(elImage, elFigWindow) {

    const PIN_CONCENTRATION = 3.0;      // higher = sharper/briefer "tug" at the view centre
    const PIN_STRENGTH = 2.0;           // pin amplitude as a multiple of G/2
    const MAX_PIN_CENTER_SPEED = 0.85;  // max on-screen speed at centre as fraction of scroll speed (prevents reversal)

    // const elImage = document.querySelector('.imageandtext__img--parallax');
    // const elFigWindow = document.querySelector('.figwindow'); // TODO: find this from parent of .imageandtext__img--parallax


    function getHeight(el) {
      return el.getBoundingClientRect().bottom - el.getBoundingClientRect().top;
    }


    function getDistanceTop(el) {
      return el.getBoundingClientRect().top;
    }


    function distanceTopAToTopB(elA, elB) {
      return getDistanceTop(elB) - getDistanceTop(elA);
    }


    function distanceBottomTo(el) {
      return el.getBoundingClientRect().bottom;
    }


    function parrallaxMove() {
      // move image
      const yVal = calcYVal();
      if (yVal === undefined || yVal === null) {
        return; // frame fully out of view: leave transform as-is
      }
      elImage.style.transform = `translateY(${yVal}px)`;
    }

    // AI coding has assisted in writing the following function.
    // The fuction has gone through testing to ensure it works better than the old code.

    // Coverage-preserving parallax with a view-anchored "pin".
    //
    // Geometry (viewport coords, down = positive):
    //   T = frame top relative to viewport, F = frame height,
    //   H = image height, V = viewport height.
    // The image sits at the frame top and is shifted by translateY(y),
    // so it spans [T + y, T + y + H].
    //
    // Motion is the image centre's offset `c` from the FRAME centre. The image's
    // on-screen position is p = frameCentre + c, and frameCentre moves up at the
    // scroll speed (~transit). To make the image LINGER at the VIEW centre we let `c`
    // rise steeply through s = 0.5 (frame centred in the view), so the image moves
    // DOWN relative to the frame there - partly cancelling the scroll and slowing the
    // image on screen (a "tug" toward tracking the viewport), then releasing.
    //
    // `c = A * g(2s-1)` where g(x) = tanh(b*x)/tanh(b) is an odd S-curve: g(0)=0
    // (image centred in frame, so its centre sits exactly at the view centre at the
    // slow point), g(+/-1)=+/-1, and PIN_CONCENTRATION (b) concentrates the swing
    // near the centre into a brief, pronounced tug. `c` is monotonic, so the image
    // never reverses on screen.
    //
    // A = PIN_STRENGTH * (H - min(F,V))/2. Because the steep part of the swing
    // happens away from the centre (in the clipped entry/exit regions where the
    // coverage window is widest), A can safely exceed the centre half-window: with
    // these proportions the clamp only engages around PIN_STRENGTH ~ 3. PIN_STRENGTH
    // >= 1 also means the full pan span (F + 2A) covers the whole image height, so no
    // part of the image is left permanently unrevealed.
    //
    // A is also capped so the on-screen speed at the view centre never exceeds
    // MAX_PIN_CENTER_SPEED * scrollSpeed. This prevents the image from racing backward
    // on screen (or reversing direction) when the image is very tall relative to the
    // frame. A final clamp guarantees no gaps. Constants are defined at the top of
    // imageParallax() so they exist before the initial parrallaxMove() call.
    function calcYVal() {
      const imgHt = getHeight(elImage);
      const figHt = getHeight(elFigWindow);
      const winHt = window.innerHeight;
      const T = getDistanceTop(elFigWindow);

      // Frame fully out of view: nothing to update.
      if ((T + figHt) < 0 || T > winHt) {
        return;
      }

      const visibleTop = Math.max(T, 0);
      const visibleBottom = Math.min(T + figHt, winHt);
      const band = visibleBottom - visibleTop;

      // Image too short to cover the visible band: centre it in the band so any
      // unavoidable gap is split evenly top/bottom.
      if (imgHt - band <= 0) {
        return ((visibleTop + visibleBottom) / 2) - T - (imgHt / 2);
      }

      const transit = winHt + figHt;
      // Progress across the viewport: 0 = entering at bottom, 1 = leaving at top,
      // 0.5 = frame centre aligned with viewport centre.
      let s = (winHt - T) / transit;
      s = Math.min(1, Math.max(0, s));

      // Odd S-curve, steep at the centre (the "pin"), flat toward the ends.
      // g(0)=0, g(+/-1)=+/-1; larger PIN_CONCENTRATION => sharper, briefer tug.
      const x = (2 * s) - 1;
      const b = PIN_CONCENTRATION;
      const g = Math.tanh(b * x) / Math.tanh(b);

      // Amplitude: desired multiple of the centre half-window, capped so the on-screen
      // speed at the view centre never exceeds MAX_PIN_CENTER_SPEED * scrollSpeed.
      // A_pin = MAX_PIN_CENTER_SPEED * transit * tanh(b) / (2b)  (from dc/ds at s=0.5).
      const A_desired = PIN_STRENGTH * ((imgHt - Math.min(figHt, winHt)) / 2);
      const A_pin = MAX_PIN_CENTER_SPEED * transit * (Math.tanh(b) / (2 * b));
      const A = Math.min(A_desired, A_pin);

      // Image-centre offset from the frame centre. `c` rises through the centre so
      // the image briefly tracks the viewport (lingers at the view centre).
      const c = A * g;
      let y = ((figHt - imgHt) / 2) + c;

      // Safety net: guarantee the image covers the visible band.
      const yHigh = visibleTop - T;
      const yLow = visibleBottom - T - imgHt;
      y = Math.min(yHigh, Math.max(yLow, y));

      return y;
    }

    return parrallaxMove;

  }
}
